<template>
  <div 
    class="contact-form"
    :style="{
      border: '1px solid #d1d5db',
      borderRadius: '0.5rem',
      backgroundColor: 'white'
    }"
  >
    <div class="flex flex-col p-2">
      <h3 class="text-sm font-semibold text-neutral-500 mb-2">
        Kontaktformular
      </h3>

      <form @submit.prevent="submitForm" class="space-y-3">
        <!-- Name Input -->
        <div>
          <label for="name" class="block text-xs font-medium text-neutral-600 mb-1">Name</label>
          <input
            type="text"
            id="name"
            v-model="formData.name"
            class="w-full bg-gray-50 px-3 py-2 text-xs text-gray-700 rounded-md"
            :style="{
              border: '1px solid #e5e7eb'
            }"
            required
          />
        </div>

        <!-- Email Input -->
        <div>
          <label for="email" class="block text-xs font-medium text-neutral-600 mb-1">E-Mail</label>
          <input
            type="email"
            id="email"
            v-model="formData.email"
            class="w-full bg-gray-50 px-3 py-2 text-xs text-gray-700 rounded-md"
            :style="{
              border: '1px solid #e5e7eb'
            }"
            required
          />
        </div>

        <!-- Message Input -->
        <div>
          <label for="message" class="block text-xs font-medium text-neutral-600 mb-1">Nachricht</label>
          <textarea
            id="message"
            v-model="formData.message"
            rows="3"
            class="w-full bg-gray-50 px-3 py-2 text-xs text-gray-700 rounded-md resize-none"
            :style="{
              border: '1px solid #e5e7eb'
            }"
            required
          ></textarea>
        </div>

        <!-- Submit Button with icon -->
        <button
          type="submit"
          class="w-full px-4 py-3 text-xs font-medium rounded-md border-none mt-2"
          :class="{ 'cursor-not-allowed opacity-70': isSubmitting, 'cursor-pointer': !isSubmitting }"
          :disabled="isSubmitting"
          :style="{
            backgroundColor: styles.bubbleColor || '#3b82f6',
            color: styles.textColor || '#ffffff',
          }"
        >
          <span v-if="isSubmitting">Wird gesendet...</span>
          <span v-else class="flex items-center justify-center space-x-1.5">
            <FlFilledMail class="h-5 w-5" />
            <span>Nachricht senden</span>
          </span>
        </button>
      </form>

      <!-- Success Message -->
      <div
        v-if="showSuccess"
        class="mt-3 p-2 bg-green-50 text-green-700 text-xs font-medium rounded-md"
        :style="{
          border: '1px solid #bbf7d0'
        }"
      >
        <div class="flex items-center space-x-2">
          <div class="bg-green-500 text-white w-4 h-4 rounded-full flex justify-center items-center text-xs font-bold flex-shrink-0">
            ✓
          </div>
          <p>Vielen Dank! Wir melden uns bald bei dir.</p>
        </div>
      </div>

      <!-- Error Message -->
      <div 
        v-if="errorMessage" 
        class="mt-3 p-2 bg-red-50 text-red-700 text-xs rounded-md"
        :style="{
          border: '1px solid #fecaca'
        }"
      >
        <p>{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
// Import the email function from the service
import { sendEmail } from "../lib/appwrite";
import { FlFilledMail } from "@kalimahapps/vue-icons";

export default {
  name: "ContactForm",
  components: {
    FlFilledMail,
  },
  props: {
    styles: {
      type: Object,
      default: () => ({
        bubbleColor: "#3b82f6",
        bubbleHoverColor: "#2563eb",
        textColor: "#ffffff",
        emailAddress: "dev@avalanche.ch",
        companyName: "Chatly",
      }),
    },
  },
  emits: ["form-submitted"],
  data() {
    return {
      formData: {
        name: "",
        email: "",
        message: "",
      },
      isSubmitting: false,
      showSuccess: false,
      errorMessage: "",
    };
  },
  computed: {
    // Get the dynamic business email from styles
    businessEmail() {
      return this.styles.emailAddress || "contact@yourcompany.com";
    },
    // Get the dynamic company name from styles
    companyName() {
      return this.styles.companyName || "Chatly";
    },
  },
  methods: {
    async submitForm() {
      try {
        // Reset any error messages
        this.errorMessage = "";

        // Basic validation
        if (
          !this.formData.name ||
          !this.formData.email ||
          !this.formData.message
        ) {
          this.errorMessage = "Bitte fülle alle Felder aus.";
          return;
        }

        // Set submitting state
        this.isSubmitting = true;

        // Log to console that we're submitting
        console.log("Contact form submitted:", {
          ...this.formData,
          businessEmail: this.businessEmail,
          companyName: this.companyName,
        });

        // Emit form data to parent component
        this.$emit("form-submitted", { ...this.formData });

        // 1. Send confirmation email to the customer
        const customerEmailContent = `
Hallo ${this.formData.name},

vielen Dank für deine Nachricht. Wir haben deine Anfrage erhalten und werden uns bald bei dir melden.

Deine Nachricht:
"${this.formData.message}"

Mit freundlichen Grüßen,
${this.companyName} Support-Team
        `;

        await sendEmail({
          to: this.formData.email,
          subject: `Bestätigung deiner Anfrage - ${this.companyName}`,
          text: customerEmailContent,
          fromEmail: "no-reply@chatly-mail.ch",
          fromName: "Chatly Benachrichtigung",
        });

        // 2. Send notification email to the business (dynamic email from styles)
        const businessEmailContent = `
Neue Kontaktanfrage über ${this.companyName} Chat Widget:

Name: ${this.formData.name}
E-Mail: ${this.formData.email}

Nachricht:
"${this.formData.message}"

---
Diese Nachricht wurde über das Chat Widget auf deiner Website gesendet.
        `;

        await sendEmail({
          to: this.businessEmail,
          subject: `Neue Kontaktanfrage - ${this.companyName}`,
          text: businessEmailContent,
          fromEmail: "no-reply@chatly-mail.ch",
          fromName: "Chatly Benachrichtigung",
        });

        // Reset form
        this.formData = {
          name: "",
          email: "",
          message: "",
        };

        // Show success message
        this.showSuccess = true;

        // Hide success message after 5 seconds
        setTimeout(() => {
          this.showSuccess = false;
        }, 5000);
      } catch (error) {
        console.error("Error sending contact form emails:", error);
        this.errorMessage =
          "Es gab ein Problem beim Senden deiner Nachricht. Bitte versuche es später noch einmal.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>